import React, { useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useAppDispatch } from '../../store';
import { toggleSpeechRecognition } from '../../store/thunks/sessionThunks';
import type { SpeechState } from '../../store/types';
import './Speech.css';

interface SpeechRecognitionProps {
  speechState: SpeechState;
  onTranscript: (transcript: string) => void;
  isProcessing: boolean;
  enabled: boolean;
}

export const SpeechRecognition = forwardRef<
  { toggleSpeech: () => void }, 
  SpeechRecognitionProps
>(({ speechState, onTranscript, isProcessing, enabled }, ref) => {
  const dispatch = useAppDispatch();
  
  // Debug logs
  useEffect(() => {
    console.log('SpeechRecognition state update:', {
      speechEnabled: speechState.isEnabled,
      speechListening: speechState.isListening,
      speechConnecting: speechState.isConnecting,
      speechError: speechState.error,
      enabledProp: enabled,
      isProcessing,
      currentTranscript: speechState.currentTranscript,
    });
  }, [speechState, enabled, isProcessing]);
  
  /**
   * Handle toggle speech function with debouncing
   */
  const handleToggleSpeech = useCallback(() => {
    console.log('Speech toggle clicked:', {
      enabled: speechState.isEnabled,
      listening: speechState.isListening,
      connecting: speechState.isConnecting,
      enabledProp: enabled,
      isProcessing,
    });
    
    // Don't toggle if not enabled or if processing
    if (!enabled || !speechState.isEnabled || isProcessing) {
      console.log('Speech toggle blocked:', {
        notEnabled: !enabled || !speechState.isEnabled,
        isProcessing,
      });
      return;
    }
    
    // Don't toggle if connecting
    if (speechState.isConnecting) {
      console.log('Speech toggle blocked - connecting');
      return;
    }
    
    // Dispatch the toggle thunk (which will handle the middleware chain)
    console.log('Dispatching toggleSpeechRecognition thunk');
    dispatch(toggleSpeechRecognition());
  }, [dispatch, speechState.isEnabled, speechState.isListening, speechState.isConnecting, enabled, isProcessing]);

  /**
   * Expose toggle function to parent component
   */
  useImperativeHandle(ref, () => ({
    toggleSpeech: handleToggleSpeech
  }), [handleToggleSpeech]);

  /**
   * Handle transcript changes - call onTranscript when we get final results
   */
  useEffect(() => {
    if (speechState.currentTranscript && speechState.isProcessingTranscript) {
      console.log('Final transcript ready:', speechState.currentTranscript);
      onTranscript(speechState.currentTranscript);
    }
  }, [speechState.currentTranscript, speechState.isProcessingTranscript, onTranscript]);

  /**
   * Get status message
   */
  const getStatusMessage = () => {
    if (!enabled) {
      return '🎤 Select a case to enable speech recognition';
    }
    
    if (!speechState.isEnabled) {
      return '🎤 Speech recognition not enabled';
    }
    
    if (speechState.isConnecting) {
      return '🔄 Connecting to speech service...';
    }
    
    if (speechState.error) {
      return `❌ Speech error: ${speechState.error}`;
    }
    
    if (isProcessing) {
      return '⏳ Processing previous question...';
    }
    
    if (speechState.isListening) {
      if (speechState.isProcessingTranscript) {
        return '⏳ Speech detected, waiting for completion...';
      }
      return '🎤 Listening continuously... Speak naturally';
    }
    
    return '🔇 Microphone muted - click unmute to start listening';
  };

  // Don't show component if speech is not supported
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return (
      <div className="speech-status offline">
        ⚠️ Speech recognition not supported in this browser
      </div>
    );
  }

  // Calculate button state for debugging
  const buttonState = {
    isEnabled: enabled && speechState.isEnabled,
    isListening: speechState.isListening,
    isConnecting: speechState.isConnecting,
    isProcessing: isProcessing,
    canToggle: enabled && speechState.isEnabled && !speechState.isConnecting && !isProcessing,
  };

  console.log('Speech button state:', buttonState);

  return (
    <div className="speech-recognition-container">
      <div 
        className={`speech-status ${speechState.isListening ? 'listening' : ''} ${speechState.error ? 'error' : ''}`}
      >
        {getStatusMessage()}
      </div>
      
      {/* Show current transcript for user feedback */}
      {speechState.currentTranscript && (
        <div className="transcript-preview">
          {speechState.isProcessingTranscript ? (
            <span className="waiting-completion">
              ⏳ "{speechState.currentTranscript}" (waiting for completion...)
            </span>
          ) : (
            <span className="interim-text">
              🎤 "{speechState.currentTranscript}"
            </span>
          )}
        </div>
      )}
      
      {/* Debug info in development */}
      {import.meta.env.DEV && (
        <div style={{ 
          fontSize: '10px', 
          color: '#666', 
          marginTop: '5px',
          padding: '2px',
          background: '#f0f0f0',
          borderRadius: '2px'
        }}>
          Debug: enabled={enabled ? 'Y' : 'N'} | 
          listening={speechState.isListening ? 'Y' : 'N'} | 
          connecting={speechState.isConnecting ? 'Y' : 'N'} | 
          processing={isProcessing ? 'Y' : 'N'} |
          error={speechState.error ? 'Y' : 'N'}
        </div>
      )}
    </div>
  );
});

SpeechRecognition.displayName = 'SpeechRecognition';